/**
 * VideoGridBlock — multi-video gallery of embed links.
 *
 * Accepts YouTube (long-form / shorts / youtu.be), Instagram (reel / p / tv),
 * TikTok, or direct .mp4 / .webm URLs — everything that `InlineVideoEmbed`
 * already knows how to play. On tap, opens the shared MediaLightbox with
 * prev/next/swipe navigation so viewers scroll through all videos without
 * closing between plays.
 *
 * Deliberately embed-only — no uploads. This keeps storage costs low
 * and creators just paste their existing YouTube / IG links.
 *
 * v149.43 — Each video may carry an optional cover (`thumbnail_url`),
 * uploaded with the same control as the single video block. The cover
 * wins; without one — or if it fails to load — the tile falls back to
 * what it always showed: the YouTube thumbnail, else a play card.
 */
import { useState } from "react";
import { Play } from "lucide-react";
import MediaLightbox from "../MediaLightbox";
import { parseEmbed } from "../InlineVideoEmbed";

const ASPECT_CLS = {
  square:    "aspect-square",
  portrait:  "aspect-[3/4]",
  landscape: "aspect-[4/3]",
  wide:      "aspect-[16/9]",
  free:      "",
};

const COL_CLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

/**
 * Best-effort thumbnail extraction. YouTube exposes a stable image URL for
 * every video ID; everything else falls back to a placeholder card.
 */
function thumbFor(embed) {
  if (!embed) return null;
  if (embed.platform === "youtube") {
    // src looks like https://www.youtube.com/embed/<id>?...
    const m = embed.src.match(/\/embed\/([\w-]+)/);
    if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
  }
  return null;
}

/**
 * v149.43 — A tile's picture: the creator's cover first, then the
 * automatic thumbnail, then nothing (the play card underneath shows).
 */
function GridThumb({ cover, auto, alt, platform }) {
  const sources = [cover, auto].filter(Boolean);
  const [at, setAt] = useState(0);
  const src = sources[at];
  if (!src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white/70 text-[10px] font-bold uppercase tracking-wider text-center px-2">
        <Play className="w-8 h-8 mb-1" strokeWidth={2} />
        {platform || "video"}
      </div>
    );
  }
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover transition-transform group-hover:scale-105"
      onError={() => setAt((n) => n + 1)}
      data-testid={src === cover ? "video-grid-cover" : "video-grid-thumb"}
    />
  );
}

export default function VideoGridBlock({ block, editable = false }) {
  const c = block?.config || {};
  const heading = (c.heading || "").trim();
  const columns = Math.max(1, Math.min(4, Number(c.columns) || 2));
  const aspect = ASPECT_CLS[c.aspect] ?? ASPECT_CLS.portrait;
  const items = Array.isArray(c.items) ? c.items : [];
  const rows = items
    .map((it) => ({ ...(it || {}), embed: parseEmbed(it?.url) }))
    .filter((r) => r.embed || (r.url || "").trim()); // keep unembeddable links visible with a fallback
  const [zoomIndex, setZoomIndex] = useState(null);

  if (rows.length === 0 && !heading) return null;

  const gridCls = `grid gap-2 sm:gap-3 ${COL_CLS[columns] || COL_CLS[2]}`;

  const lightboxItems = rows.map((r) => {
    if (r.embed?.kind === "iframe") {
      return { kind: "iframe", src: r.embed.src, caption: r.caption, aspect: r.embed.aspect };
    }
    if (r.embed?.kind === "video") {
      return { kind: "video", src: r.embed.src, caption: r.caption };
    }
    // Non-embeddable — show as an external link inside the lightbox.
    return { kind: "iframe", src: r.url, caption: r.caption };
  });

  return (
    <div
      className="ah-card video-grid-card p-4 sm:p-5"
      data-testid={`ah-block-video-grid-${block?.block_id || "preview"}`}
      style={{
        background: "var(--ah-block-bg)",
        color: "var(--ah-block-fg)",
        border: "var(--ah-block-border, 2px solid #111)",
        borderRadius: "var(--ah-block-radius, 0)",
        boxShadow: "var(--ah-block-shadow, none)",
      }}
    >
      {heading && (
        <h3
          className="font-black text-xl sm:text-2xl mb-3 leading-tight"
          style={{
            fontFamily: "var(--ah-title-family)",
            fontWeight: "var(--ah-title-weight, 900)",
            letterSpacing: "var(--ah-title-tracking, normal)",
          }}
          data-testid="video-grid-heading"
        >
          {heading}
        </h3>
      )}
      {rows.length > 0 ? (
        <div className={gridCls} data-testid="video-grid-items">
          {rows.map((r, i) => {
            const thumb = thumbFor(r.embed);
            const cover = typeof r.thumbnail_url === "string" ? r.thumbnail_url.trim() : "";
            return (
              <button
                key={i}
                type="button"
                onClick={() => (editable ? null : setZoomIndex(i))}
                className={`relative overflow-hidden border-2 border-current bg-black group ${aspect}`}
                data-testid={`video-grid-item-${i}`}
                aria-label={r.caption || `Video ${i + 1}`}
              >
                <GridThumb
                  key={`${cover}|${thumb || ""}`}
                  cover={cover}
                  auto={thumb}
                  alt={r.caption || `Video ${i + 1}`}
                  platform={r.embed?.platform}
                />
                {/* Play badge overlay */}
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="border-2 border-white bg-black/50 rounded-full p-2">
                    <Play className="w-5 h-5 text-white fill-white" strokeWidth={2} />
                  </span>
                </span>
                {r.caption && (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[11px] font-bold px-2 py-1 truncate">
                    {r.caption}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm opacity-60 text-center py-8" data-testid="video-grid-empty">
          Paste YouTube, Instagram, TikTok or MP4 links in the editor to fill this grid.
        </p>
      )}

      {zoomIndex !== null && !editable && (
        <MediaLightbox
          items={lightboxItems}
          startIndex={zoomIndex}
          onClose={() => setZoomIndex(null)}
          testid="video-grid-lightbox"
        />
      )}
    </div>
  );
}
